/**
 * Keeps the Conversations list in step with the chat (SE-8865).
 * @remarks
 * A finished chat was missing from the sidebar after New Chat because nothing
 * refetched `SearchMyThreads` once the sidebar had loaded. This hook records
 * the finished chat as a provisional row (see `provisionalThreads.ts`) and
 * refetches the list, on completion and again on New Chat. The provisional
 * row is shown until the server returns the same id.
 */

import { useApolloClient, useReactiveVar } from "@apollo/client";
import { useCallback, useEffect } from "react";

import {
  buildProvisionalThread,
  provisionalThreadsVar,
  upsertProvisionalThread,
} from "../utils/provisionalThreads";

import { SearchMyThreadsDocument } from "@/generated/graphql";
import { messagesVar, selectedThreadIdVar } from "@/stores/knowledgeGraph";

/** Thread list operations the chat calls. */
interface UseThreadListSyncReturn {
  /**
   * Records the finished chat under the server's thread id (from the stream's
   * `start` frame) and refetches the list. Without an id it only refetches.
   */
  recordCompletedTurn: (threadId: string | null) => void;
  /** Refetches the list. */
  refreshThreadList: () => Promise<void>;
}

/**
 * Empties the provisional rows.
 * @returns A resolved promise, as Apollo's store listeners expect
 */
const clearProvisionalThreads = (): Promise<void> => {
  provisionalThreadsVar([]);
  return Promise.resolve();
};

/**
 * Hook that records finished chats in the Conversations list and refetches it.
 * @returns The record and refresh operations
 */
export const useThreadListSync = (): UseThreadListSyncReturn => {
  const client = useApolloClient();
  const selectedThreadId = useReactiveVar(selectedThreadIdVar);
  const messages = useReactiveVar(messagesVar);
  const provisionalRows = useReactiveVar(provisionalThreadsVar);

  // A row the server now lists is retired by the field policy's `merge`.
  const refreshThreadList = useCallback(async () => {
    try {
      await client.refetchQueries({ include: [SearchMyThreadsDocument] });
    } catch {
      // The list keeps what it shows; the next refresh tries again.
    }
  }, [client]);

  const recordCompletedTurn = useCallback(
    (threadId: string | null) => {
      // A selected thread is one the server already lists, and a stream that
      // announced no thread id gives nothing to key the row by.
      const provisional =
        selectedThreadIdVar() || !threadId
          ? null
          : buildProvisionalThread(
              threadId,
              messagesVar(),
              new Date().toISOString()
            );
      if (provisional) {
        provisionalThreadsVar(
          upsertProvisionalThread(provisionalThreadsVar(), provisional)
        );
      }
      void refreshThreadList();
    },
    [refreshThreadList]
  );

  // Sign-out clears the store and a team switch resets it. A provisional row
  // belongs to the user and organization that made it, so it goes too.
  useEffect(() => {
    const stopClear = client.onClearStore(clearProvisionalThreads);
    const stopReset = client.onResetStore(clearProvisionalThreads);
    return () => {
      stopClear();
      stopReset();
    };
  }, [client]);

  // Pressing a provisional row selects it and clears the chat while the
  // sidebar asks the server for the thread, which the server cannot return
  // until its write lands. Put the chat's own messages back so it does not
  // open empty; if the server does return the thread, its messages replace
  // these when they arrive.
  useEffect(() => {
    if (!selectedThreadId || messages.length > 0) return;
    const row = provisionalRows.find(({ id }) => id === selectedThreadId);
    if (row) messagesVar([...row.transcript]);
  }, [selectedThreadId, messages, provisionalRows]);

  return { recordCompletedTurn, refreshThreadList };
};
